import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, User, Eye, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Alert, AlertDescription } from '../ui/alert';
import { api, Category, CategoryAuditDto } from '../../services/api';

interface CategoryAuditDialogProps {
  category: Category;
  open: boolean;
  onClose: () => void;
}

const ACTION_LABELS = {
  CREATE: 'Created',
  UPDATE: 'Updated', 
  DEACTIVATE: 'Deactivated',
  DELETE: 'Deleted'
} as const;

const ACTION_COLORS = {
  CREATE: 'bg-green-100 text-green-800 border-green-200',
  UPDATE: 'bg-blue-100 text-blue-800 border-blue-200',
  DEACTIVATE: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  DELETE: 'bg-red-100 text-red-800 border-red-200'
} as const;

export const CategoryAuditDialog: React.FC<CategoryAuditDialogProps> = ({
  category,
  open,
  onClose
}) => {
  const [auditTrail, setAuditTrail] = useState<CategoryAuditDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAuditTrail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getCategoryAuditTrail(category.id);
      setAuditTrail(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  }, [category.id]);

  useEffect(() => {
    if (open && category.id) {
      loadAuditTrail();
    }
  }, [open, category.id, loadAuditTrail]);

  const formatChangeValues = (beforeValues?: string, afterValues?: string) => {
    if (!beforeValues && !afterValues) return null;

    try {
      const before = beforeValues ? JSON.parse(beforeValues) : {};
      const after = afterValues ? JSON.parse(afterValues) : {};
      
      const changes: { field: string; before: any; after: any }[] = [];
      
      // Compare all fields
      const allFields = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
      
      for (const field of allFields) {
        if (before[field] !== after[field]) {
          changes.push({
            field,
            before: before[field],
            after: after[field]
          });
        }
      }
      
      return changes;
    } catch {
      return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Audit Trail: {category.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {loading && (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!loading && !error && (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-4">
                {auditTrail.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <div className="text-4xl mb-2">📋</div>
                    <p>No audit records found</p>
                  </div>
                ) : (
                  auditTrail.map((record, index) => {
                    const changes = formatChangeValues(record.beforeValues, record.afterValues);
                    
                    return (
                      <div
                        key={record.id}
                        className="border rounded-lg p-4 bg-card relative"
                      >
                        {/* Timeline connector */}
                        {index < auditTrail.length - 1 && (
                          <div className="absolute left-6 top-12 bottom-0 w-px bg-border" />
                        )}
                        
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            <Badge 
                              className={ACTION_COLORS[record.action]}
                              variant="outline"
                            >
                              {ACTION_LABELS[record.action]}
                            </Badge>
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <User className="h-3 w-3" />
                                <span>{record.changedBy}</span>
                              </div>
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                <span>
                                  {new Date(record.changedAt).toLocaleString()}
                                </span>
                              </div>
                            </div>

                            {record.changeReason && (
                              <div className="mb-3 p-2 bg-muted rounded text-sm">
                                <strong>Reason:</strong> {record.changeReason}
                              </div>
                            )}

                            {changes && changes.length > 0 && (
                              <div className="space-y-2">
                                <h4 className="text-sm font-medium">Changes:</h4>
                                <div className="grid gap-2">
                                  {changes.map((change) => (
                                    <div 
                                      key={change.field}
                                      className="flex items-center gap-2 text-sm"
                                    >
                                      <span className="font-medium capitalize min-w-[80px]">
                                        {change.field}:
                                      </span>
                                      <div className="flex items-center gap-2">
                                        {change.before !== undefined && (
                                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">
                                            {change.before || '(empty)'}
                                          </span>
                                        )}
                                        {change.before !== undefined && change.after !== undefined && (
                                          <span className="text-muted-foreground">→</span>
                                        )}
                                        {change.after !== undefined && (
                                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                            {change.after || '(empty)'}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          )}

          <div className="flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};